import { Injector } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { flattenObject } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { FormID } from '@finxone-platform/form-action';
import { Store } from '@ngxs/store';
import { parsePhoneNumber } from 'libphonenumber-js';
import { firstValueFrom } from 'rxjs';
import { UpdateFormDataActionWithId } from '../../../actions/form-submission.action';
import { FormActionState } from '../../../state/form-submision.state';
import { ProfileState } from '../../../state/user-profile.state';

export async function assignProfileValuesToForm(injector: Injector, formGroup: FormGroup): Promise<void> {
  await firstValueFrom(formGroup.statusChanges);
  const store = injector.get(Store);

  const existingFormData =
    store.selectSnapshot(FormActionState.getFormActionStateWithId(FormID.UPDATE_USER_PROFILE))?.formData ||
    {};

  const profileData = store.selectSnapshot(ProfileState.getProfile);
  if (!profileData) return;

  const parsedPhone = parsePhoneNumber(profileData?.phoneNumber || '');

  // Spread the extraAttributes keys onto the top level of the profile payload so they can be
  // matched against form controls. Other nested object keys (e.g. orgList) are left untouched
  // since they are not needed while updating the profile.
  const flattenProfileDataExtraAttributes = profileData?.extraAttributes || {};
  const profilePayload = {
    ...profileData,
    ...flattenProfileDataExtraAttributes,
    marketingPreferences: booleanKeysToArray(profileData?.marketingPreferences ?? {}),
    countryCode: parsedPhone?.countryCallingCode ? `+${parsedPhone.countryCallingCode}` : undefined,
    phoneNumber: parsedPhone?.nationalNumber,
    email: profileData.email,
    verifiedEmail: profileData.emailVerified ? profileData.email : '',
    verifiedCountryCode: parsedPhone?.countryCallingCode ? `+${parsedPhone.countryCallingCode}` : undefined,
    verifiedPhone: profileData.phoneVerified ? parsedPhone?.nationalNumber : '',
    isEmailVerified: profileData.emailVerified,
    isPhoneVerified: profileData.phoneVerified,
    dob: profileData.dob,
  } as any;

  const formKeys = Object.keys(formGroup.controls);

  /* 
  When merging form data, we use `existingFormData` if it has a value; otherwise, we use `profile` sate data.
  A potential issue occurs if a user changes data, navigates to another page, and then return back to same page,the page might still display the original `profile` data instead of the user's changes.
  To address this, we check if `stateValue` has been updated. 
  - If it has, we use the updated value from `UPDATE_USER_PROFILE`.
  - If not, we fall back to the original value from `profile`.
  */
  const mergedFormData: Record<string, any> = {};
  formKeys.forEach((key) => {
    const existingValue = existingFormData[key];
    const profileValue = profilePayload[key];

    mergedFormData[key] =
      existingValue !== undefined && existingValue !== null && existingValue !== ''
        ? existingValue
        : profileValue;
  });

  //Add verification flags to the merged form data only if they are not already present in the existing state.
  //This ensures that we initialize `isEmailVerified` and `isPhoneVerified` with the current profile values,
  if (!existingFormData.hasOwnProperty('isEmailVerified')) {
    mergedFormData['isEmailVerified'] = profileData.emailVerified;
  }
  if (!existingFormData.hasOwnProperty('isPhoneVerified')) {
    mergedFormData['isPhoneVerified'] = profileData.phoneVerified;
  }

  // Patch form for any controls that exist
  const filteredPayload = Object.fromEntries(
    Object.entries(mergedFormData).filter(([key, value]) => formGroup.controls[key]?.value !== value),
  );

  if (Object.keys(filteredPayload).length > 0) {
    formGroup.patchValue(filteredPayload, { emitEvent: true });
  }

  store.dispatch(new UpdateFormDataActionWithId(mergedFormData, FormID.UPDATE_USER_PROFILE));
}

/**
 * Converts an object with boolean values into an array of keys
 * where the value is `true`.
 *
 * @template T - A generic type extending Record<string, boolean>
 * @param obj - The object to process (optional)
 * @returns An array of keys (strings) whose values are true
 */
export function booleanKeysToArray<T extends Record<string, boolean>>(obj?: T): string[] {
  if (!obj) return [];
  return (Object.keys(obj) as Array<keyof T>).filter((key) => obj[key] === true).map(String);
}
